/**
 * Sign-in bootstrap and sign-out cleanup: the callables App.tsx runs around
 * Firebase Auth, and the Firestore cache reset on sign-out.
 */
import { httpsCallable } from 'firebase/functions'
import { terminate, clearIndexedDbPersistence } from 'firebase/firestore'
import { db, functions } from '../config'

/** Active read-only impersonation session (staff "view as tenant"). */
export interface ImpersonationContext {
  orgId: string
  orgName: string
  clinicId?: string | null
  clinicName?: string | null
  role: 'admin' | 'manager' | 'user'
  mode: 'view' | 'edit'
  impUntil: number | null
}

/** Context returned by ensureUserContext callable. */
export interface UserContext {
  orgId: string | null
  role: string
  clinicId: string | null
  /** True for VitalRev internal staff accounts. */
  staff?: boolean
  /** Present when staff is actively viewing a customer tenant. */
  impersonating?: ImpersonationContext | null
}

/** Redeems an invite token for the signed-in user. Throws the callable's HttpsError. */
export async function acceptInvite(token: string): Promise<void> {
  const fn = httpsCallable<{ token: string }, unknown>(functions, 'acceptInvite')
  await fn({ token })
}

/** Idempotent login bootstrap: creates or syncs the user's org/clinic/claims. */
export async function ensureUserContext(): Promise<UserContext> {
  const fn = httpsCallable<Record<string, never>, UserContext>(functions, 'ensureUserContext')
  const res = await fn({})
  return res.data
}

/**
 * Stops the Firestore client and deletes its IndexedDB cache, so the next user
 * on this browser never sees the previous user's cached docs. Throws when
 * another tab holds the database; callers treat it as best-effort.
 */
export async function clearFirestoreCache(): Promise<void> {
  await terminate(db)
  await clearIndexedDbPersistence(db)
}
